import { __rest, __assign } from 'tslib';
import React, { useEffect, useMemo } from 'react';
import cn from 'classnames';
import { usePrevious } from '@alfalab/hooks';
import { ConfirmationContext } from '../../context.js';
import { defaultTexts } from '../../types.js';
import { useCountdown, ONE_MINUTE, ONE_DAY } from '../../utils.js';
import { Initial } from '../screens/initial/component.js';
import { Hint } from '../screens/hint/component.js';
import { FatalError } from '../screens/fatal-error/component.js';
import { TempBlock } from '../screens/temp-block/component.js';
import '../../../../button/esm/mobile';
import '../../../../code-input/esm/desktop';
import '../../../../code-input/esm/mobile';
import '../../../../link/esm';
import '../../../../typography/esm';
import '../header/component.js';
import '../../countdown-section-7e1d30f1.js';
import '../../../../button/esm';
import '../../../../spinner/esm';
import '../countdown-loader/component.js';

var styles = {"component":"confirmation__component_weev8"};
require('./index.css');

var confirmationScreens = {
    INITIAL: Initial,
    HINT: Hint,
    FATAL_ERROR: FatalError,
    TEMP_BLOCK: TempBlock,
};
var BaseConfirmation = function (_a) {
    var state = _a.state, screen = _a.screen, _b = _a.alignContent, alignContent = _b === void 0 ? 'left' : _b; _a.children; var _c = _a.requiredCharAmount, requiredCharAmount = _c === void 0 ? 5 : _c, _d = _a.countdownDuration, countdownDuration = _d === void 0 ? ONE_MINUTE : _d, _e = _a.tempBlockDuration, tempBlockDuration = _e === void 0 ? ONE_DAY : _e, phone = _a.phone, blockSmsRetry = _a.blockSmsRetry, dataTestId = _a.dataTestId, className = _a.className, getScreensMap = _a.getScreensMap, onInputFinished = _a.onInputFinished, onChangeState = _a.onChangeState, onSmsRetryClick = _a.onSmsRetryClick, onChangeScreen = _a.onChangeScreen, onFatalErrorOkButtonClick = _a.onFatalErrorOkButtonClick, onTempBlockFinished = _a.onTempBlockFinished, mobile = _a.mobile, _f = _a.clearCodeOnError, clearCodeOnError = _f === void 0 ? true : _f, _g = _a.hideCountdownSection, hideCountdownSection = _g === void 0 ? false : _g, breakpoint = _a.breakpoint, client = _a.client, initialScreenHintSlot = _a.initialScreenHintSlot, errorVisibleDuration = _a.errorVisibleDuration, restProps = __rest(_a, ["state", "screen", "alignContent", "children", "requiredCharAmount", "countdownDuration", "tempBlockDuration", "phone", "blockSmsRetry", "dataTestId", "className", "getScreensMap", "onInputFinished", "onChangeState", "onSmsRetryClick", "onChangeScreen", "onFatalErrorOkButtonClick", "onTempBlockFinished", "mobile", "clearCodeOnError", "hideCountdownSection", "breakpoint", "client", "initialScreenHintSlot", "errorVisibleDuration"]);
    var _h = useCountdown(countdownDuration), timeLeft = _h[0], startTimer = _h[1], stopTimer = _h[2];
    var prevState = usePrevious(state);
    useEffect(function () {
        startTimer();
    }, [startTimer]);
    useEffect(function () {
        /**
         * Перезапускаем таймер после повторного запроса кода
         */
        if (state === 'INITIAL' && prevState === 'CODE_SENDING') {
            startTimer();
        }
    }, [state, prevState, startTimer]);
    useEffect(function () {
        /**
         * Останавливаем таймер, если новый экран/состояние не содержит таймер
         */
        if (!['INITIAL', 'HINT', 'TEMP_BLOCK'].includes(screen) || blockSmsRetry) {
            stopTimer();
        }
    }, [state, screen, blockSmsRetry, stopTimer]);
    var handleSmsRetry = function () {
        onChangeState('CODE_SENDING');
        onSmsRetryClick();
    };
    var handleInputFinished = function (code) {
        onChangeState('CODE_CHECKING');
        onInputFinished(code);
    };
    var handleFatalErrorOkButtonClick = function () {
        if (onFatalErrorOkButtonClick) {
            onFatalErrorOkButtonClick();
        }
    };
    // eslint-disable-next-line react/jsx-no-constructed-context-values
    var contextValue = {
        hideCountdownSection: hideCountdownSection,
        alignContent: alignContent,
        texts: __assign(__assign({}, defaultTexts), restProps.texts),
        state: state,
        screen: screen,
        requiredCharAmount: requiredCharAmount,
        countdownDuration: countdownDuration,
        timeLeft: timeLeft,
        tempBlockDuration: tempBlockDuration,
        phone: phone,
        blockSmsRetry: blockSmsRetry,
        breakpoint: breakpoint,
        client: client,
        onTempBlockFinished: onTempBlockFinished,
        onChangeState: onChangeState,
        onChangeScreen: onChangeScreen,
        clearCodeOnError: clearCodeOnError,
        errorVisibleDuration: errorVisibleDuration,
        initialScreenHintSlot: initialScreenHintSlot,
        onInputFinished: handleInputFinished,
        onSmsRetryClick: handleSmsRetry,
        onFatalErrorOkButtonClick: handleFatalErrorOkButtonClick,
    };
    var customScreen = useMemo(function () { return getScreensMap && getScreensMap(confirmationScreens); }, [getScreensMap]);
    var screensMap = customScreen || confirmationScreens;
    var CurrentScreen = screensMap[screen];
    return (React.createElement(ConfirmationContext.Provider, { value: contextValue },
        React.createElement("div", { className: cn(styles.component, className), "data-test-id": dataTestId }, CurrentScreen && React.createElement(CurrentScreen, { mobile: mobile }))));
};

export { BaseConfirmation };
